<template>
  <div class="flex flex-col text-left">
    <div
      class="w-full"
      :class="{ 'mt-3 rounded-b-2xl bg-white py-5 ': !uiStore.mobile }"
    >
      <label v-if="!uiStore.mobile" class="px-5 text-left">
        <span class="font-semibold">Filter results</span>
      </label>
      <p v-if="!uiStore.mobile" class="px-5 text-sm font-normal">
        Further refine your search to narrow your results
      </p>
      <div class="flex flex-col">
        <v-expansion-panels :class="{ 'z-[501]': uiStore.mobile }">
          <v-expansion-panel>
            <v-expansion-panel-title class="px-1 py-0">
              <template #actions>
                <fa-icon icon="fa-solid fa-chevron-down" class="pr-2" />
              </template>
              <p class="m-0 ml-3 w-full text-left font-semibold">
                Skills<span
                  v-if="skills.length != 0"
                  class="text-vgorange ml-1 text-xs"
                >
                  ({{ skills.length }})</span
                >
              </p>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="px-2">
              <v-autocomplete
                v-model="skills"
                v-model:search="skillObj.searchInput"
                :loading="skillObj.loading"
                :items="skillObj.items"
                variant="outlined"
                :color="vgTeal"
                flat
                clearable
                multiple
                closable-chips
                hide-no-data
                hide-details
                @update:model-value="skillObj.searchInput = ''"
              >
                <template #item="{ item, props }">
                  <v-list-item>
                    <v-chip class="bg-vgsoftpurple m-1 ml-0" v-bind="props"
                      >{{ item.raw.title }}
                    </v-chip>
                  </v-list-item>
                </template>
                <template #chip="{ item }">
                  <v-chip class="bg-vgsoftpurple m-1 ml-0">{{
                    item.raw.title
                  }}</v-chip>
                </template>
              </v-autocomplete>
            </v-expansion-panel-text>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-title class="px-1 py-0">
              <template #actions>
                <fa-icon icon="fa-solid fa-chevron-down" class="pr-2" />
              </template>
              <p class="m-0 ml-3 w-full text-left font-semibold">
                Tools<span
                  v-if="tools.length != 0"
                  class="text-vgorange ml-1 text-xs"
                >
                  ({{ tools.length }})</span
                >
              </p>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="px-2">
              <v-autocomplete
                v-model="tools"
                v-model:search="toolObj.searchInput"
                :loading="toolObj.loading"
                :items="toolObj.items"
                variant="outlined"
                :color="vgTeal"
                flat
                clearable
                multiple
                closable-chips
                hide-no-data
                hide-details
                @update:model-value="toolObj.searchInput = ''"
              >
                <template #item="{ item, props }">
                  <v-list-item>
                    <v-chip class="bg-vgsoftcyan m-1 ml-0" v-bind="props"
                      >{{ item.raw.title }}
                    </v-chip>
                  </v-list-item>
                </template>
                <template #chip="{ item }">
                  <v-chip class="bg-vgsoftcyan m-1 ml-0">{{
                    item.raw.title
                  }}</v-chip>
                </template>
              </v-autocomplete>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel class="border-b-2">
            <v-expansion-panel-title class="px-1 py-0">
              <template #actions>
                <fa-icon icon="fa-solid fa-chevron-down" class="pr-2" />
              </template>
              <p class="m-0 ml-3 w-full text-left font-semibold">
                Timezone
                <span v-if="!!timezone" class="text-vgorange text-xs">(1)</span>
              </p>
            </v-expansion-panel-title>
            <v-expansion-panel-text v-if="googleReady" class="px-2">
              <div class="flex">
                <label
                  v-if="checkoutStore.matchingCriteria.timezone"
                  class="flex-1 font-bold"
                >
                  {{ checkoutStore.matchingCriteria.timezone || '-' }}
                </label>
                <fa-icon
                  v-if="checkoutStore.matchingCriteria.timezone"
                  icon="fa-regular fa-xmark-large"
                  @click="clearTimezone"
                />
              </div>
              <vue-google-autocomplete
                id="map"
                ref="addressInput"
                :class="{ addressError: googleLocationNotFound }"
                placeholder="Search city to set"
                types="(cities)"
                autocomplete="off"
                classname="city-autocomplete"
                @placechanged="cityPicked"
                @no-results-found="googleLocationNotFound = true"
                @keypress.enter.prevent
              ></vue-google-autocomplete>
            </v-expansion-panel-text>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-title class="px-1 py-0">
              <template #actions>
                <fa-icon icon="fa-solid fa-chevron-down" class="pr-2" />
              </template>
              <p class="m-0 ml-3 w-full text-left font-semibold">
                Timezone Range
                <span v-if="!!offset" class="text-vgorange text-xs"
                  >+/- {{ offset.title }} hours</span
                >
              </p>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="px-2">
              <v-select
                v-model="offset"
                :items="offset_range"
                :color="vgTeal"
                variant="outlined"
                placeholder="0"
                density="compact"
                :disabled="!timezone"
              >
              </v-select>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-title class="px-1 py-0">
              <template #actions>
                <fa-icon icon="fa-solid fa-chevron-down" class="pr-2" />
              </template>
              <p class="m-0 ml-3 w-full text-left font-semibold">
                Self Identification<span
                  v-if="selfIdentifications.length != 0"
                  class="text-vgorange ml-1 text-xs"
                >
                  ({{ selfIdentifications.length }})</span
                >
              </p>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="px-2">
              <v-select
                v-model="selfIdentifications"
                class="w-[130]"
                :items="selfIdentificationsItems"
                :color="vgTeal"
                variant="outlined"
                multiple
                chips
                clearable
                density="compact"
              >
                <template #chip="{ item, index }">
                  <v-chip v-if="index === 0" size="small" class="w-30">
                    <span class="max-w-[130px] truncate">{{
                      item.raw.title
                    }}</span>
                  </v-chip>
                  <span v-if="index === 1" class="truncate text-xs">
                    (+{{ selfIdentifications.length - 1 }} others)
                  </span>
                </template>
              </v-select>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
        <div class="flex justify-end py-3">
          <button
            class="text-vgocean pr-5 text-sm font-semibold uppercase underline"
            vg-type="secondary"
            @click="clearFilters"
          >
            reset filter
          </button>
          <vg-btn v-if="false" class="bg-vgocean" @click="applyFilters"
            >apply</vg-btn
          >
        </div>
      </div>
    </div>

    <!--

      <label class="text-sm">
        Availability
        <v-select
          v-model="availabilities"
          :items="availabilityItems"
          :color="vgTeal"
          outlined
          multiple
          chips
          clearable
        >
        </v-select>
      </label>
      <label class="text-sm">
        Timezone
        <v-select
          v-model="timezone"
          :items="validTimeZones"
          :color="vgTeal"
          outlined
          chips
          clearable
        >
        </v-select>
      </label> -->
    <!-- </div> -->
  </div>
</template>

<script>
import tailwind from 'tailwind.config';
import { mapStores, storeToRefs } from 'pinia';
import { useCheckoutStore } from '@/stores/checkout';
import { useUiStore } from '@/stores/ui';
import { useUserStore } from '@/stores/user';
import gmapsInit from '@/utils/gmaps';
import { getTimezone } from '@/services/userService';
import VueGoogleAutocomplete from 'vue-google-autocomplete';
import { checkoutHoursItems, offset_range } from '@/config';
import { searchCriteria } from '@/services/matchingService';

export default {
  name: 'VaSearchFilters',
  components: { VueGoogleAutocomplete },
  emits: ['updateRoleHours'],
  data() {
    const { vaConfig } = storeToRefs(useUserStore());

    return {
      vaConfig,
      vgTeal: tailwind.theme.extend.colors.vgteal[500],
      vgBlack: '#000000',
      loading: false,
      // assistants: [],
      // validTimeZones,
      googleReady: false,
      googleLocationNotFound: false,
      // selectedLanguages: undefined,
      // selectedAvailabilities: undefined,
      checkoutHoursItems,
      toolObj: {
        model: null,
        searchInput: '',
        items: [],
        selectedItems: [],
        loading: false,
      },
      skillObj: {
        model: null,
        searchInput: '',
        items: [],
        selectedItems: [],
        loading: false,
      },
      offset_range,
    };
  },
  computed: {
    ...mapStores(useCheckoutStore),
    ...mapStores(useUiStore),
    selfIdentificationsItems() {
      return this.vaConfig?.self_identifications_ids
        .filter(
          (id) =>
            id.value !== 'None of the above' &&
            id.value !== 'Prefer not to disclose'
        )
        .map((id) => {
          return { title: id.value, value: id.id.toString() };
        });
    },
    // availabilityItems() {
    //   return this.vaConfig?.va_availabilities_ids.map((id) => {
    //     return { title: id.value, value: id.id.toString() };
    //   });
    // },
    // languageItems() {
    //   return this.vaConfig?.languages.map((language) => {
    //     return { title: language, value: language };
    //   });
    // },
    selfIdentifications: {
      get() {
        return this.checkoutStore.matchingCriteria.self_identifications
          ? this.checkoutStore.matchingCriteria.self_identifications?.split(',')
          : '';
      },
      async set(value) {
        await this.checkoutStore.updateMatchingCriteria({
          ...this.checkoutStore.matchingCriteria,
          self_identifications: value.filter((x) => x).join(',') || undefined,
        });
      },
    },
    offset: {
      get() {
        return this.checkoutStore.matchingCriteria.offset_range
          ? this.offset_range.find(
              (range) =>
                range.value === this.checkoutStore.matchingCriteria.offset_range
            )
          : '';
      },
      async set(value) {
        await this.checkoutStore.updateMatchingCriteria({
          ...this.checkoutStore.matchingCriteria,
          offset_range: value,
        });
      },
    },
    tools: {
      get() {
        return this.checkoutStore.matchingCriteria.tids
          ? this.checkoutStore.matchingCriteria?.tids?.map((tid) => Number(tid))
          : '';
      },
      async set(value) {
        this.toolObj.selectedItems = this.toolObj.items.filter((item) =>
          value.includes(item.value)
        );

        this.checkoutStore.setSelectedTools(
          this.toolObj.selectedItems.map((item) => ({
            id: item.value,
            keyword: item.title,
          }))
        );
        await this.checkoutStore.updateMatchingCriteria({
          ...this.checkoutStore.matchingCriteria,
          tids: [...new Set(value.filter((x) => x))] || undefined,
        });
      },
    },
    skills: {
      get() {
        return this.checkoutStore.matchingCriteria.sids
          ? this.checkoutStore.matchingCriteria.sids?.map((sid) => Number(sid))
          : '';
      },
      async set(value) {
        this.skillObj.selectedItems = this.skillObj.items.filter((item) =>
          value.includes(item.value)
        );
        this.checkoutStore.setSelectedSkills(
          this.skillObj.selectedItems.map((item) => ({
            id: item.value,
            keyword: item.title,
          }))
        );
        await this.checkoutStore.updateMatchingCriteria({
          ...this.checkoutStore.matchingCriteria,
          sids: [...new Set(value.filter((x) => x))] || undefined,
        });
      },
    },

    // availabilities: {
    //   get() {
    //     return this.checkoutStore.matchingCriteria.availabilities
    //       ? this.checkoutStore.matchingCriteria.availabilities?.split(',')
    //       : '';
    //   },
    //   async set(value) {
    //     this.selectedAvailabilities = value.join(',');

    //     await this.checkoutStore.updateMatchingCriteria({
    //       ...this.checkoutStore.matchingCriteria,
    //       availabilities: this.selectedAvailabilities,
    //     });
    //   },
    // },
    // languages: {
    //   get() {
    //     return this.checkoutStore.matchingCriteria.languages?.split(',');
    //   },
    //   set(value) {
    //     this.selectedLanguages = value.filter((x) => x).join(',') || undefined;
    //   },
    // },
    timezone: {
      get() {
        return this.checkoutStore.matchingCriteria.timezone;
      },
    },
  },
  watch: {
    'toolObj.searchInput'(val) {
      val &&
        !this.toolObj.items.find((item) => item.title === val) &&
        this.handleCriteriaSearch('tool', val);
    },
    'skillObj.searchInput'(val) {
      val &&
        !this.skillObj.items.find((item) => item.title === val) &&
        this.handleCriteriaSearch('skill', val);
    },
    async 'checkoutStore.matchingCriteria.sids'() {
      if (
        this.checkoutStore.matchingCriteria?.sids &&
        this.checkoutStore.matchingCriteria.sids.length >
          this.skillObj.selectedItems.length
      ) {
        await this.updateSkillItemsById();
      }
    },
    async 'checkoutStore.matchingCriteria.tids'() {
      if (
        this.checkoutStore.matchingCriteria?.tids &&
        this.checkoutStore.matchingCriteria.tids.length >
          this.toolObj.selectedItems.length
      ) {
        await this.updateToolItemsById();
      }
    },
  },
  async created() {
    gmapsInit().then(() => {
      this.googleReady = true;
    });

    await this.updateToolItemsById();
    await this.updateSkillItemsById();
  },
  methods: {
    // async applyFilters() {
    //   await this.checkoutStore.updateMatchingCriteria({
    //     ...this.checkoutStore.matchingCriteria,
    //     timezone: this.selectedTimezone,
    //     languages: this.selectedLanguages,
    //     availabilities: this.selectedAvailabilities,
    //   });
    // },
    async clearFilters() {
      // this.selectedAvailabilities = undefined;
      // this.selectedLanguages = undefined;

      await this.checkoutStore.updateMatchingCriteria(
        JSON.parse(
          JSON.stringify({
            ...this.checkoutStore.matchingCriteria,
            timezone: '',
            timezone_offset: '',
            offset_range: '',
            self_identifications: '',
            // languages: '',
            // availabilities: '',
            tids: [],
            sids: [],
          })
        )
      );
      if (this.$refs.addressInput) {
        this.$refs.addressInput.clear();
      }

      this.toolObj = {
        searchInput: '',
        items: [],
        selectedItems: [],
        loading: false,
      };
      this.skillObj = {
        searchInput: '',
        items: [],
        selectedItems: [],
        loading: false,
      };
    },
    updateRoleHours(value) {
      this.$emit('updateRoleHours', value);
    },
    async cityPicked(args) {
      this.location = args;
      await getTimezone(this.location.latitude, this.location.longitude)
        .then((result) => {
          this.checkoutStore.updateMatchingCriteria({
            ...this.checkoutStore.matchingCriteria,
            timezone: result.timezone,
            timezone_offset: result.timezone_offset,
            offset_range: 120,
          });
        })
        .catch((error) => {
          this.$root.$snackbar.error(
            error?.data?.message || `Could not get timezone and timezone offset`
          );
        });
      this.googleLocationNotFound = false;
    },
    async handleCriteriaSearch(criteriaType, searchQuery) {
      this.toolObj.loading = true;
      let searchResult = null;
      if (criteriaType === 'tool') {
        searchResult = await searchCriteria([
          `searchQuery=${searchQuery}`,
          `types[]=tool`,
          `pagination.sortBy[]=value`,
        ]).catch((error) => {
          this.$root.$snackbar.error(
            error?.data?.message ?? 'An Error occurred, please try again later.'
          );

          return [];
        });
      } else if (criteriaType === 'skill') {
        searchResult = await searchCriteria([
          `searchQuery=${searchQuery}`,
          `types[]=skill`,
          `pagination.sortBy[]=value`,
        ]).catch((error) => {
          this.$root.$snackbar.error(
            error?.data?.message ?? 'An Error occurred, please try again later.'
          );

          return [];
        });
      }
      this[criteriaType + 'Obj'].items = searchResult.map((row) => ({
        title: row.value,
        value: row.id,
      }));
      this[criteriaType + 'Obj'].items = [
        ...this[criteriaType + 'Obj'].items,
        ...this[criteriaType + 'Obj'].selectedItems,
      ];
      this.toolObj.loading = false;
    },
    clearTimezone() {
      this.checkoutStore.updateMatchingCriteria({
        ...this.checkoutStore.matchingCriteria,
        timezone: '',
        timezone_offset: '',
        offset_range: 0,
      });
      this.$refs.addressInput?.clear();
    },
    async updateSkillItemsById() {
      try {
        this.skillObj.items = await searchCriteria([
          `ids=${this.checkoutStore.matchingCriteria?.sids}`,
          `types[]=skill`,
        ]).then((data) =>
          data.map((item) => ({ title: item.value, value: item.id }))
        );
        this.skillObj.selectedItems = [
          ...this.skillObj.items,
          ...this.skillObj.selectedItems,
        ];
      } catch (error) {
        this.skillObj.items = [];
      }
    },
    async updateToolItemsById() {
      try {
        this.toolObj.items = await searchCriteria([
          `ids=${this.checkoutStore.matchingCriteria?.tids}`,
          `types[]=tool`,
        ]).then((data) =>
          data.map((item) => ({ title: item.value, value: item.id }))
        );
        this.toolObj.selectedItems = [
          ...this.toolObj.items,
          ...this.toolObj.selectedItems,
        ];
      } catch (error) {
        this.toolObj.items = [];
      }
    },
  },
};
</script>
<style scoped>
.v-expansion-panel {
  @apply border-b;
}
* :deep(.v-expansion-panel .v-expansion-panel__shadow),
* :deep(.v-expansion-panels--variant-default > .v-expansion-panel::after) {
  @apply hidden;
}
* :deep(.v-expansion-panel-title__overlay) {
  @apply shadow-none opacity-0 hidden;
}
* :deep(.v-theme--light.v-expansion-panels .v-expansion-panel) {
  background-color: rgba(0, 0, 0, 0);
}
*
  :deep(.v-expansion-panel--active
    > .v-expansion-panel-title--active
    .v-expansion-panel-title__icon:not(.v-expansion-panel-title__icon--disable-rotate)) {
  -webkit-transform: rotate(-180deg);
  transform: rotate(-180deg);
}
*
  :deep(.v-expansion-panel
    > .v-expansion-panel-title
    .v-expansion-panel-title__icon:not(.v-expansion-panel-title__icon--disable-rotate)) {
  @apply transition-transform;
}
</style>
